import React from 'react';
import { ArrowRight } from 'lucide-react';
import RoboticHandAnimation from './RoboticHandAnimation';

// Splits a phrase into <span> words with staggered fade-in-up delay.
const RevealWords = ({ text, baseDelay = 0, wordStep = 0.08, className = '' }) => {
  const parts = text.split(/(\s+)/); // keep whitespace
  let wIdx = 0;
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (/^\s+$/.test(p)) return p;
        const delay = baseDelay + wIdx * wordStep;
        wIdx += 1;
        return (
          <span
            key={i}
            className="hero-word-reveal"
            style={{ animationDelay: `${delay}s` }}
          >
            {p}
          </span>
        );
      })}
    </span>
  );
};

const Hero = () => {
  const openQualifyChat = () => {
    window.dispatchEvent(new CustomEvent('open-qualify-chat'));
  };

  return (
    <section className="hero-section">
      <div className="hero-content">
        <div className="hero-left">
          <h1 className="display-huge hero-headline">
            <RevealWords text="Wir digitalisieren Deine Agentur." baseDelay={0.05} wordStep={0.08} />
            <br />
            <span className="hero-highlight">
              <RevealWords text="Wir automatisieren deine Leads." baseDelay={0.45} wordStep={0.09} />
            </span>
          </h1>

          <div className="hero-cta-row hero-fade-in" style={{ animationDelay: '0.85s' }}>
            <button
              type="button"
              onClick={openQualifyChat}
              className="hero-cta-btn"
              data-testid="hero-cta-termin"
              aria-label="Jetzt Termin sichern"
            >
              <span className="hero-cta-btn-text">Jetzt Termin sichern</span>
              <span className="hero-cta-btn-icon">
                <ArrowRight size={16} strokeWidth={2.6} />
              </span>
            </button>
            <span className="hero-cta-hint">
              <span className="hero-cta-hint-dot" />
              Kostenloses Erstgespräch · unverbindlich
            </span>
          </div>

          <p className="body-large hero-subtext hero-fade-in" style={{ animationDelay: '1.05s' }}>
            Schneller reagieren, mehr Abschlüsse, 50% mehr Termine – mit KI, WhatsApp-Automationen und deinem eigenen CRM.
          </p>
          <p className="body-large hero-subtext hero-fade-in" style={{ marginTop: '20px', fontWeight: 600, animationDelay: '1.25s' }}>
            <span style={{ 
              background: 'linear-gradient(135deg, #00FFD1 0%, #00D4FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>DIE KI-MASTERCLASS</span> → Ein schlüsselfertiges System, das Versicherungsagenturen schneller macht, die Prozesse optimiert und mehr Umsatz bringt.
          </p>
        </div>

          <div className="hero-right">
          <RoboticHandAnimation />
        </div>
      </div>

      <div className="hero-grid-overlay"></div>

      <style>{`
        .hero-word-reveal {
          display: inline-block;
          opacity: 0;
          transform: translateY(14px);
          animation: hero-word-in 0.75s cubic-bezier(.22,1,.36,1) forwards;
          will-change: opacity, transform;
        }
        .hero-fade-in {
          opacity: 0;
          animation: hero-fade-in 0.9s cubic-bezier(.22,1,.36,1) forwards;
        }
        /* Hero primary CTA button */
        .hero-cta-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-top: 28px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        .hero-cta-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px 14px 26px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #00FFD1 0%, #00D4FF 55%, #7C3AED 130%);
          background-size: 180% 100%;
          background-position: 0% 50%;
          color: #061014;
          font-size: 15px;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: none;
          cursor: pointer;
          overflow: hidden;
          isolation: isolate;
          box-shadow:
            0 0 0 1px rgba(0, 255, 209, 0.5),
            0 0 24px rgba(0, 255, 209, 0.45),
            0 12px 34px rgba(0, 212, 255, 0.28);
          transition: transform .35s cubic-bezier(.22,1,.36,1),
                      box-shadow .35s ease,
                      background-position .6s ease;
          animation: hero-cta-pulse 3.2s ease-in-out infinite;
        }
        .hero-cta-btn:hover {
          transform: translateY(-2px);
          background-position: 100% 50%;
          box-shadow:
            0 0 0 1px rgba(0, 255, 209, 0.75),
            0 0 34px rgba(0, 255, 209, 0.75),
            0 18px 46px rgba(0, 212, 255, 0.42);
        }
        .hero-cta-btn:active { transform: translateY(0) scale(0.98); }
        .hero-cta-btn::before {
          /* Traveling sheen highlight */
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%);
          transform: translateX(-100%);
          transition: transform 0.85s cubic-bezier(.22,1,.36,1);
          z-index: 1;
          pointer-events: none;
        }
        .hero-cta-btn:hover::before { transform: translateX(100%); }
        .hero-cta-btn-text,
        .hero-cta-btn-icon { position: relative; z-index: 2; }
        .hero-cta-btn-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform .3s cubic-bezier(.22,1,.36,1);
        }
        .hero-cta-btn:hover .hero-cta-btn-icon { transform: translateX(4px); }
        @keyframes hero-cta-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(0, 255, 209, 0.5),
              0 0 20px rgba(0, 255, 209, 0.42),
              0 12px 34px rgba(0, 212, 255, 0.24);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(0, 255, 209, 0.7),
              0 0 30px rgba(0, 255, 209, 0.65),
              0 12px 34px rgba(0, 212, 255, 0.38);
          }
        }
        .hero-cta-hint {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.7);
          letter-spacing: 0.02em;
        }
        .hero-cta-hint-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #00FFD1;
          box-shadow: 0 0 12px #00FFD1;
          animation: hero-cta-hint-pulse 2s ease-in-out infinite;
        }
        @keyframes hero-cta-hint-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.35); }
        }
        @keyframes hero-word-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-word-reveal, .hero-fade-in {
            animation: none;
            opacity: 1;
            transform: none;
          }
          .hero-cta-btn,
          .hero-cta-hint-dot { animation: none; }
        }
        @media (max-width: 640px) {
          .hero-cta-row { gap: 14px; margin-top: 22px; }
          .hero-cta-btn { font-size: 14px; padding: 12px 20px 12px 22px; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
